import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  CloseRounded,
  DeleteRounded,
  EditRounded,
  GroupsRounded,
  PersonAddAltRounded,
  WorkspacesRounded,
} from '@mui/icons-material';
import {
  createIndustrialWorkflowWorkArea,
  deleteIndustrialWorkflowWorkArea,
  listIndustrialWorkflowAssignableUsers,
  listIndustrialWorkflowWorkAreaMembers,
  listIndustrialWorkflowWorkAreas,
  replaceIndustrialWorkflowWorkAreaMembers,
  updateIndustrialWorkflowWorkArea,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowAssignableUser,
  IndustrialWorkflowWorkArea,
  IndustrialWorkflowWorkAreaUpsertPayload,
} from '../types/industrialWorkflows';

type WorkAreaFormState = {
  name: string;
  description: string;
  color_key: string;
  display_order: string;
  is_active: boolean;
};

const EMPTY_WORK_AREA_FORM: WorkAreaFormState = {
  name: '',
  description: '',
  color_key: 'blue',
  display_order: '0',
  is_active: true,
};

const WORK_AREA_COLORS: Record<string, string> = {
  slate: '#94a3b8',
  blue: '#38bdf8',
  amber: '#fbbf24',
  green: '#4ade80',
  red: '#f87171',
  violet: '#a78bfa',
};

function parseInteger(
  value: string,
  fallback = 0,
) {
  const parsedValue = Number.parseInt(
    value,
    10,
  );

  return Number.isFinite(parsedValue)
    ? parsedValue
    : fallback;
}

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function getAssignableUserLabel(
  user: IndustrialWorkflowAssignableUser,
) {
  return (
    user.full_name ||
    user.email ||
    user.user_id
  );
}

function buildWorkAreaPayload(
  form: WorkAreaFormState,
): IndustrialWorkflowWorkAreaUpsertPayload {
  return {
    name: form.name,
    description:
      form.description.trim() || null,
    color_key:
      form.color_key.trim() || null,
    display_order: parseInteger(
      form.display_order,
    ),
    is_active: form.is_active,
    settings: {},
  };
}

export default function IndustrialWorkflowsWorkAreasConfigSection() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [
    areaDialogOpen,
    setAreaDialogOpen,
  ] = useState(false);

  const [
    editingArea,
    setEditingArea,
  ] = useState<IndustrialWorkflowWorkArea | null>(
    null,
  );

  const [
    areaForm,
    setAreaForm,
  ] = useState<WorkAreaFormState>(
    EMPTY_WORK_AREA_FORM,
  );

  const [
    areaPendingDelete,
    setAreaPendingDelete,
  ] = useState<IndustrialWorkflowWorkArea | null>(
    null,
  );

  const [
    membersDialogArea,
    setMembersDialogArea,
  ] = useState<IndustrialWorkflowWorkArea | null>(
    null,
  );

  const [
    selectedMemberUserIds,
    setSelectedMemberUserIds,
  ] = useState<string[]>([]);

  const workAreasQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-areas',
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkAreas(true),
  });

  const assignableUsersQuery = useQuery({
    queryKey: [
      'industrial-workflows-assignable-users',
    ],
    queryFn:
      listIndustrialWorkflowAssignableUsers,
  });

  const areaMembersQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-area-members',
      membersDialogArea?.id,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkAreaMembers(
        membersDialogArea?.id as string,
      ),
    enabled: Boolean(membersDialogArea),
    retry: false,
  });

  useEffect(() => {
    if (!membersDialogArea) {
      setSelectedMemberUserIds([]);
      return;
    }

    setSelectedMemberUserIds(
      (areaMembersQuery.data ?? []).map(
        (member) => member.user_id,
      ),
    );
  }, [
    areaMembersQuery.data,
    membersDialogArea,
  ]);

  const assignableUserById = useMemo(
    () =>
      new Map(
        (assignableUsersQuery.data ?? []).map(
          (user) => [
            user.user_id,
            user,
          ],
        ),
      ),
    [assignableUsersQuery.data],
  );

  const selectedMembers = useMemo(
    () =>
      selectedMemberUserIds
        .map((userId) =>
          assignableUserById.get(userId),
        )
        .filter(
          (
            user,
          ): user is IndustrialWorkflowAssignableUser =>
            Boolean(user),
        ),
    [
      assignableUserById,
      selectedMemberUserIds,
    ],
  );

  const createAreaMutation = useMutation({
    mutationFn: (
      payload: IndustrialWorkflowWorkAreaUpsertPayload,
    ) =>
      createIndustrialWorkflowWorkArea(
        payload,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-work-areas',
        ],
      });

      setAreaDialogOpen(false);
      setEditingArea(null);
      setAreaForm(EMPTY_WORK_AREA_FORM);

      enqueueSnackbar(
        'Área creada correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const updateAreaMutation = useMutation({
    mutationFn: ({
      workAreaId,
      payload,
    }: {
      workAreaId: string;
      payload: IndustrialWorkflowWorkAreaUpsertPayload;
    }) =>
      updateIndustrialWorkflowWorkArea(
        workAreaId,
        payload,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-work-areas',
        ],
      });

      setAreaDialogOpen(false);
      setEditingArea(null);
      setAreaForm(EMPTY_WORK_AREA_FORM);

      enqueueSnackbar(
        'Área actualizada correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const deleteAreaMutation = useMutation({
    mutationFn: (
      workAreaId: string,
    ) =>
      deleteIndustrialWorkflowWorkArea(
        workAreaId,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-work-areas',
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-my-work-area-memberships',
          ],
          refetchType: 'all',
        }),
      ]);

      setAreaPendingDelete(null);
      setAreaDialogOpen(false);
      setEditingArea(null);
      setAreaForm(EMPTY_WORK_AREA_FORM);

      enqueueSnackbar(
        'Área eliminada correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const replaceMembersMutation = useMutation({
    mutationFn: ({
      workAreaId,
      userIds,
    }: {
      workAreaId: string;
      userIds: string[];
    }) =>
      replaceIndustrialWorkflowWorkAreaMembers(
        workAreaId,
        {
          user_ids: userIds,
        },
      ),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-work-area-members',
            variables.workAreaId,
          ],
        }),

        queryClient.invalidateQueries({
          queryKey: [
            'industrial-workflows-my-work-area-memberships',
          ],
          refetchType: 'all',
        }),
      ]);

      setMembersDialogArea(null);
      setSelectedMemberUserIds([]);

      enqueueSnackbar(
        'Miembros del área actualizados.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(
        getErrorMessage(error),
        {
          variant: 'error',
        },
      );
    },
  });

  const handleCreateArea = () => {
    setEditingArea(null);
    setAreaForm(EMPTY_WORK_AREA_FORM);
    setAreaDialogOpen(true);
  };

  const handleEditArea = (
    area: IndustrialWorkflowWorkArea,
  ) => {
    setEditingArea(area);

    setAreaForm({
      name: area.name,
      description:
        area.description ?? '',
      color_key:
        area.color_key ?? 'blue',
      display_order: String(
        area.display_order,
      ),
      is_active: area.is_active,
    });

    setAreaDialogOpen(true);
  };

  const handleCloseAreaDialog = () => {
    if (
      createAreaMutation.isPending ||
      updateAreaMutation.isPending
    ) {
      return;
    }

    setAreaDialogOpen(false);
    setEditingArea(null);
    setAreaForm(EMPTY_WORK_AREA_FORM);
  };

  const handleSaveArea = () => {
    if (!areaForm.name.trim()) {
      enqueueSnackbar(
        'Debes indicar el nombre del área.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    const payload =
      buildWorkAreaPayload(areaForm);

    if (editingArea) {
      updateAreaMutation.mutate({
        workAreaId: editingArea.id,
        payload,
      });

      return;
    }

    createAreaMutation.mutate(payload);
  };

  const handleOpenMembers = (
    area: IndustrialWorkflowWorkArea,
  ) => {
    setSelectedMemberUserIds([]);
    setMembersDialogArea(area);
  };

  const handleCloseMembersDialog = () => {
    if (replaceMembersMutation.isPending) {
      return;
    }

    setMembersDialogArea(null);
    setSelectedMemberUserIds([]);
  };

  const handleSaveMembers = () => {
    if (!membersDialogArea) return;

    replaceMembersMutation.mutate({
      workAreaId: membersDialogArea.id,
      userIds: selectedMemberUserIds,
    });
  };

  const handleConfirmDeleteArea = () => {
    if (!areaPendingDelete) {
      return;
    }

    deleteAreaMutation.mutate(
      areaPendingDelete.id,
    );
  };

  const areaMutationIsPending =
    createAreaMutation.isPending ||
    updateAreaMutation.isPending ||
    deleteAreaMutation.isPending;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha(
            '#38bdf8',
            0.18,
          )}`,
          bgcolor: 'rgba(15,23,42,0.7)',
          p: {
            xs: 1.75,
            md: 2.25,
          },
        }}
      >
        <Stack spacing={1.6}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
            spacing={1.4}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <WorkspacesRounded
                sx={{
                  color: '#38bdf8',
                }}
              />

              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: '1.08rem',
                  }}
                >
                  Áreas de trabajo
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.84rem',
                    mt: 0.25,
                  }}
                >
                  Define las áreas responsables y sus
                  miembros. Cada empresa puede crear
                  libremente su propia estructura.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              startIcon={<AddRounded />}
              onClick={handleCreateArea}
              sx={{
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              Nueva área
            </Button>
          </Stack>

          <Divider
            sx={{
              borderColor: alpha(
                '#94a3b8',
                0.14,
              ),
            }}
          />

          {areaDialogOpen ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#38bdf8',
                  0.22,
                )}`,
                bgcolor: 'rgba(15,23,42,0.54)',
                px: 1.6,
                py: 1.45,
              }}
            >
              <Stack spacing={1.4}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {editingArea
                      ? 'Editar área'
                      : 'Nueva área'}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={handleCloseAreaDialog}
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    <CloseRounded fontSize="small" />
                  </IconButton>
                </Stack>

                <Divider
                  sx={{
                    borderColor: alpha(
                      '#94a3b8',
                      0.12,
                    ),
                  }}
                />

                <TextField
                  size="small"
                  label="Nombre"
                  value={areaForm.name}
                  onChange={(event) =>
                    setAreaForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  required
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Descripción"
                  value={areaForm.description}
                  onChange={(event) =>
                    setAreaForm((current) => ({
                      ...current,
                      description:
                        event.target.value,
                    }))
                  }
                  multiline
                  minRows={2}
                  fullWidth
                />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                    },
                    gap: 1.2,
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="Color"
                    value={areaForm.color_key}
                    onChange={(event) =>
                      setAreaForm((current) => ({
                        ...current,
                        color_key:
                          event.target.value,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="slate">
                      Gris
                    </MenuItem>

                    <MenuItem value="blue">
                      Azul
                    </MenuItem>

                    <MenuItem value="amber">
                      Ámbar
                    </MenuItem>

                    <MenuItem value="green">
                      Verde
                    </MenuItem>

                    <MenuItem value="red">
                      Rojo
                    </MenuItem>

                    <MenuItem value="violet">
                      Violeta
                    </MenuItem>
                  </TextField>

                  <TextField
                    size="small"
                    label="Orden visual"
                    value={areaForm.display_order}
                    onChange={(event) =>
                      setAreaForm((current) => ({
                        ...current,
                        display_order:
                          event.target.value,
                      }))
                    }
                    type="number"
                    fullWidth
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={areaForm.is_active}
                      onChange={(event) =>
                        setAreaForm((current) => ({
                          ...current,
                          is_active:
                            event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Área activa"
                />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  {editingArea ? (
                    <Button
                      color="error"
                      startIcon={<DeleteRounded />}
                      disabled={areaMutationIsPending}
                      onClick={() =>
                        setAreaPendingDelete(editingArea)
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 850,
                      }}
                    >
                      Eliminar área
                    </Button>
                  ) : (
                    <Box />
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                  >
                    <Button
                      onClick={handleCloseAreaDialog}
                      disabled={areaMutationIsPending}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      Cancelar
                    </Button>

                    <Button
                      variant="contained"
                      disabled={areaMutationIsPending}
                      onClick={handleSaveArea}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      Guardar
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {workAreasQuery.isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : null}

          {workAreasQuery.isError ? (
            <Alert
              severity="error"
              variant="outlined"
            >
              {getErrorMessage(
                workAreasQuery.error,
              )}
            </Alert>
          ) : null}

          {!workAreasQuery.isLoading &&
          !workAreasQuery.isError &&
          !(workAreasQuery.data ?? []).length ? (
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                bgcolor:
                  'rgba(14,116,144,0.08)',
              }}
            >
              Todavía no hay áreas configuradas.
              Crea la primera para comenzar a
              organizar responsabilidades.
            </Alert>
          ) : null}

          {(workAreasQuery.data ?? []).length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1.1,
              }}
            >
              {(workAreasQuery.data ?? []).map(
                (area) => {
                  const areaColor =
                    WORK_AREA_COLORS[
                      area.color_key ?? 'slate'
                    ] ??
                    WORK_AREA_COLORS.slate;

                  return (
                    <Paper
                      key={area.id}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(
                          areaColor,
                          0.26,
                        )}`,
                        bgcolor: alpha(
                          areaColor,
                          0.055,
                        ),
                        px: 1.35,
                        py: 1.25,
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent:
                                'center',
                              color: areaColor,
                              bgcolor: alpha(
                                areaColor,
                                0.14,
                              ),
                              border: `1px solid ${alpha(
                                areaColor,
                                0.3,
                              )}`,
                              flexShrink: 0,
                            }}
                          >
                            <GroupsRounded fontSize="small" />
                          </Box>

                          <Box
                            sx={{
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.6}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 900,
                                }}
                              >
                                {area.name}
                              </Typography>

                              {!area.is_active ? (
                                <Chip
                                  size="small"
                                  label="Inactiva"
                                  sx={{
                                    color: '#cbd5e1',
                                    bgcolor: alpha(
                                      '#64748b',
                                      0.14,
                                    ),
                                  }}
                                />
                              ) : null}
                            </Stack>

                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.75rem',
                                mt: 0.25,
                              }}
                            >
                              Orden {area.display_order}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            aria-label="Editar área"
                            onClick={() =>
                              handleEditArea(area)
                            }
                            sx={{
                              color: '#94a3b8',
                            }}
                          >
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Stack>

                        {area.description ? (
                          <Typography
                            sx={{
                              color: '#94a3b8',
                              fontSize: '0.82rem',
                            }}
                          >
                            {area.description}
                          </Typography>
                        ) : null}

                        <Divider
                          sx={{
                            borderColor: alpha(
                              '#94a3b8',
                              0.12,
                            ),
                          }}
                        />

                        <Button
                          size="small"
                          startIcon={
                            <PersonAddAltRounded />
                          }
                          onClick={() =>
                            handleOpenMembers(area)
                          }
                          sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontWeight: 850,
                          }}
                        >
                          Gestionar miembros
                        </Button>
                      </Stack>
                    </Paper>
                  );
                },
              )}
            </Box>
          ) : null}
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(areaPendingDelete)}
        onClose={() => {
          if (!deleteAreaMutation.isPending) {
            setAreaPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Eliminar área de trabajo
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography>
              Vas a eliminar el área{' '}
              <strong>
                {areaPendingDelete?.name}
              </strong>
              .
            </Typography>

            <Alert severity="warning" variant="outlined">
              Solo se permitirá si el área no está utilizada
              por ninguna etapa o tarea. Sus asociaciones de
              miembros dejarán de estar disponibles.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={deleteAreaMutation.isPending}
            onClick={() => setAreaPendingDelete(null)}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteRounded />}
            disabled={deleteAreaMutation.isPending}
            onClick={handleConfirmDeleteArea}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(membersDialogArea)}
        onClose={handleCloseMembersDialog}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          sx: {
            bgcolor: 'rgba(2,6,23,0.76)',
            backdropFilter: 'blur(6px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: `1px solid ${alpha(
              '#38bdf8',
              0.2,
            )}`,
            bgcolor: '#111c30',
            boxShadow:
              '0 28px 80px rgba(0,0,0,0.46)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 2.2,
              py: 1.6,
              borderBottom: `1px solid ${alpha(
                '#94a3b8',
                0.14,
              )}`,
            }}
          >
            <GroupsRounded
              sx={{
                color: '#38bdf8',
              }}
            />

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Miembros del área
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                  mt: 0.15,
                }}
              >
                {membersDialogArea?.name ?? 'Área'}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleCloseMembersDialog}
              sx={{
                color: '#94a3b8',
              }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2.2,
            py: 2,
          }}
        >
          <Stack spacing={1.6}>
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.88rem',
              }}
            >
              Selecciona los usuarios que forman
              parte del área. Una persona puede
              pertenecer a varias áreas.
            </Typography>

            {areaMembersQuery.isLoading ||
            assignableUsersQuery.isLoading ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <CircularProgress size={18} />

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.86rem',
                  }}
                >
                  Cargando usuarios...
                </Typography>
              </Stack>
            ) : null}

            {areaMembersQuery.isError ? (
              <Alert
                severity="error"
                variant="outlined"
              >
                {getErrorMessage(
                  areaMembersQuery.error,
                )}
              </Alert>
            ) : null}

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={
                assignableUsersQuery.data ?? []
              }
              value={selectedMembers}
              getOptionLabel={
                getAssignableUserLabel
              }
              isOptionEqualToValue={(
                option,
                value,
              ) =>
                option.user_id ===
                value.user_id
              }
              onChange={(_, value) =>
                setSelectedMemberUserIds(
                  value.map(
                    (user) => user.user_id,
                  ),
                )
              }
              renderOption={(
                props,
                option,
                { selected },
              ) => (
                <li {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{
                      mr: 1,
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                      }}
                    >
                      {getAssignableUserLabel(
                        option,
                      )}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.75rem',
                      }}
                    >
                      {option.has_workflows_access
                        ? 'Acceso activo al organizador'
                        : 'Sin acceso al organizador'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Usuarios del área"
                  placeholder="Seleccionar usuarios"
                />
              )}
            />

            <Alert
              severity="info"
              variant="outlined"
            >
              Los usuarios sin acceso al
              organizador pueden quedar asociados
              al área, pero necesitarán permisos
              antes de operar tareas.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.2,
            py: 1.4,
            borderTop: `1px solid ${alpha(
              '#94a3b8',
              0.12,
            )}`,
          }}
        >
          <Button
            onClick={handleCloseMembersDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              replaceMembersMutation.isPending
            }
            onClick={handleSaveMembers}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            Guardar miembros
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}